/** Utility functions for blog content processing */
import DOMPurify from 'dompurify';

interface FirestoreTimestamp {
  _seconds: number;
  _nanoseconds: number;
}

/** Convert Firestore serialized timestamp to Date */
export function timestampToDate(ts: FirestoreTimestamp | string | Date): Date {
  if (ts instanceof Date) return ts;
  if (typeof ts === 'string') return new Date(ts);
  if (ts && typeof ts === 'object' && '_seconds' in ts) {
    return new Date(ts._seconds * 1000 + ts._nanoseconds / 1_000_000);
  }
  return new Date();
}

/** Strip HTML tags from a string */
export function stripHtml(html: string): string {
  if (!html) return '';
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').replace(/\s+/g, ' ').trim();
}

/** Clean a blog title (decode entities, trim) — no innerHTML to prevent XSS */
export function cleanTitle(title: string): string {
  if (!title) return '';
  return title
    .replace(/&amp;/g, '&')
    .replace(/&lt;/g, '<')
    .replace(/&gt;/g, '>')
    .replace(/&quot;/g, '"')
    .replace(/&#39;/g, "'")
    .replace(/&#x27;/g, "'")
    .replace(/&#x2F;/g, '/')
    .trim();
}

/** Sanitize blog HTML content using DOMPurify — keeps safe formatting tags */
export function cleanBlogContent(html: string): string {
  if (!html) return '';
  return DOMPurify.sanitize(html, {
    ALLOWED_TAGS: [
      'p', 'br', 'b', 'i', 'em', 'strong', 'u', 'a', 'ul', 'ol', 'li',
      'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'blockquote', 'pre', 'code',
      'img', 'figure', 'figcaption', 'table', 'thead', 'tbody', 'tr', 'th', 'td',
      'div', 'span', 'hr', 'sup', 'sub', 'mark'
    ],
    ALLOWED_ATTR: ['href', 'src', 'alt', 'title', 'class', 'id', 'target', 'rel', 'width', 'height'],
    ALLOW_DATA_ATTR: false
  });
}

/** Get a plain-text excerpt from HTML content */
export function getExcerpt(html: string, maxLength = 160): string {
  const text = stripHtml(html);
  if (text.length <= maxLength) return text;
  return text.slice(0, maxLength).replace(/\s+\S*$/, '') + '…';
}

/** Estimate read time in minutes from HTML content */
export function getReadTime(html: string): number {
  const words = stripHtml(html).split(/\s+/).length;
  return Math.max(1, Math.round(words / 200));
}
